import { useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { CombinedGraphQLErrors } from '@apollo/client';
import { GraphQLErrorCode, type GraphQLErrorCodeType } from '@insio/validation';
import { hasCompanyRegistry } from '@insio/validation';
import { CountryCode } from '@insio/countries';
import {
  GetProfileDocument,
  type PreferencesInput,
  type SupportedLocale,
  type UpdatePartyInput,
  UpdateAccountDocument,
  UpdatePartyDocument
} from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { useCompanyRegistryFill } from '@/hooks/use-company-registry-fill';
import { partyErrorMessage } from '@/utils/party-issues';
import {
  MasterAccountDeclarationErrorReason,
  masterAccountDeclarationErrorReason
} from './master-account-declaration-error';

/** The organization's preferences as this page writes them; its language is never cleared. */
type OrganizationPreferencesInput = Omit<PreferencesInput, 'locale'> & {
  locale?: SupportedLocale;
};

function isIdentityReadOnly(error: unknown): boolean {
  return (
    masterAccountDeclarationErrorReason(error) ===
    MasterAccountDeclarationErrorReason.OWN_BOOKS_IDENTITY_READ_ONLY
  );
}

function hasErrorCode(error: unknown, code: GraphQLErrorCodeType): boolean {
  if (!CombinedGraphQLErrors.is(error)) return false;
  return error.errors.some(gqlError => gqlError.extensions?.['code'] === code);
}

/** The account's effective currencies come from the profile, so changing one refetches it. */
function changesCurrency(input: UpdatePartyInput): boolean {
  return 'accountingCurrencyCode' in input || 'reportingCurrencyCode' in input;
}

export function useOrganizationSettings() {
  const t = useFeatureTranslation('systemSettings.organization');
  const { i18n } = useTranslation();
  const { showSuccess, showError } = useToastHelpers();

  const { data, loading, error } = useQuery(GetProfileDocument, { fetchPolicy: 'network-only' });

  const [updateAccount] = useMutation(UpdateAccountDocument);
  const [updateParty] = useMutation(UpdatePartyDocument);

  const account = data?.profile?.account;
  const mainParty = account?.mainParty;
  const addr = mainParty?.mainAddress;
  const preferences = mainParty?.preferences;
  const noParty = !mainParty;
  // The master account's own books follow the board's identity; only currencies and preferences stay.
  const identityReadOnly = !!account?.isMasterAccountOwnBooks;

  // An organization whose country is not set yet is treated as Czech, so the
  // lookup is offered before the country field has been touched.
  const country = addr?.countryCode || CountryCode.CZ;
  const lookupCountry = hasCompanyRegistry(country) ? country : null;
  const hasCompanyId = !!mainParty?.companyId;

  const registryFill = useCompanyRegistryFill({
    countryCode: lookupCountry,
    partyId: mainParty?.id
  });

  // ── Save helpers ──────────────────────────────────────────────────────

  const saveErrorMessage = (error: unknown): string => {
    if (isIdentityReadOnly(error)) return t('masterAccountErrors.ownBooksIdentityReadOnly');
    if (hasErrorCode(error, GraphQLErrorCode.ACCOUNT_NAME_TAKEN)) {
      return t('errors.accountNameTaken');
    }
    if (hasErrorCode(error, GraphQLErrorCode.ORGANIZATION_ACCOUNTING_CURRENCY_REQUIRED)) {
      return t('errors.accountingCurrencyRequired');
    }
    return t('feedback.saveError');
  };

  const saveAccountName = async (name: string) => {
    if (!name.trim()) throw new Error('required');
    try {
      // Backend mirrors the name onto the main party atomically; the returned
      // account carries the updated mainParty, so Apollo refreshes its cache.
      const { error: updateError } = await updateAccount({ variables: { input: { name } } });
      // errorPolicy 'all' resolves a GraphQL refusal instead of throwing it.
      if (updateError) throw updateError;
      showSuccess(t('feedback.saveSuccess'));
    } catch (e) {
      showError(saveErrorMessage(e));
      throw e;
    }
  };

  /** Writes party fields. Returns false when the backend reported validation errors. */
  const writePartyFields = async (input: UpdatePartyInput): Promise<boolean> => {
    if (!mainParty?.id) return false;
    const { data: updated, error: updateError } = await updateParty({
      variables: { id: mainParty.id, input },
      ...(changesCurrency(input) && { refetchQueries: [{ query: GetProfileDocument }] })
    });
    if (updateError) throw updateError;
    const result = updated?.updateParty.result;
    if (result && result.errorCount > 0) {
      showError(partyErrorMessage(result.issues) || t('feedback.saveError'));
      return false;
    }
    return true;
  };

  const savePartyField = async (input: UpdatePartyInput) => {
    if (!mainParty?.id) return;
    try {
      if (await writePartyFields(input)) {
        showSuccess(t('feedback.saveSuccess'));
      }
    } catch (e) {
      showError(saveErrorMessage(e));
      // eslint-disable-next-line no-console
      console.warn('Error saving party field:', e);
    }
  };

  const saveAddressField =
    (field: 'streetName' | 'buildingNumber' | 'city' | 'postalCode' | 'countryCode') =>
    async (value: string) => {
      await savePartyField({ mainAddress: { [field]: value } });
    };

  const savePartyPreferencesAsync = async (input: OrganizationPreferencesInput) => {
    if (!mainParty?.id) return;

    try {
      const result = (
        await updateParty({
          variables: { id: mainParty.id, input: { preferences: input } },
          refetchQueries: [{ query: GetProfileDocument }],
          awaitRefetchQueries: true
        })
      ).data?.updateParty.result;

      if (result && result.errorCount > 0) {
        showError(partyErrorMessage(result.issues) || t('feedback.saveError'));
        return;
      }

      // Switch UI language only after the mutation succeeds.
      if (input.locale) {
        const newLocale = input.locale.toLowerCase();
        if (newLocale !== i18n.language) void i18n.changeLanguage(newLocale);
      }

      showSuccess(t('feedback.saveSuccess'));
    } catch (e) {
      showError(t('feedback.saveError'));
      // eslint-disable-next-line no-console
      console.warn('Error saving party preferences:', e);
    }
  };

  const savePartyPreferences = (input: OrganizationPreferencesInput) => {
    void savePartyPreferencesAsync(input);
  };

  return {
    t,
    loading,
    error,
    account,
    mainParty,
    addr,
    preferences,
    noParty,
    identityReadOnly,
    saveAccountName,
    savePartyField,
    saveAddressField,
    savePartyPreferences,
    // Company-registry lookup
    lookupCountry,
    hasCompanyId,
    registryLoading: registryFill.loading,
    registryError: registryFill.error,
    registryApplied: registryFill.applied,
    applyRegistryLookup: registryFill.apply,
    clearRegistryFeedback: registryFill.clearFeedback
  };
}
